import {
  Alert,
  Box,
  CircularProgress,
  Collapse,
  Paper,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { useGetWorkByIdQuery } from "../../store/services/timelineApiRTK";
import { parseEventDataArray } from "../../utils/eventDataParser";
import AddCommentDialog from "./AddCommentDialog";
import WorkActivityStream from "./WorkActivityStream";
import WorkModalHeader from "./components/WorkModalHeader";
import WorkInfoBar from "./components/WorkInfoCard";
import CommunicationCard from "./components/CommunicationCard";
import workTypeRegistry from "./work-types";
import { WorkTypeEnum } from "../../store/types/work.types";
import NextActivityCard from "./components/NextActivityCard";
import FilesCard from "./components/FilesCard";
import { FILE_EVENT_TYPES } from "./components/fileEventTypes";
import WorkModalFooter from "./components/WorkModalFooter";

const PANEL_WIDTH = 700;
const MINIMIZED_WIDTH = 280;
const MOBILE_MINIMIZED_WIDTH = 160;

interface WorkModalProps {
  workId: number;
  minimized: boolean;
  isFocused?: boolean;
  onClose: () => void;
  onToggleMinimize: () => void;
  onFocus?: () => void;
  panelOffset?: number;
  showBackToActivity?: boolean;
  onBackToActivity?: () => void;
}

const WorkModal = ({
  workId,
  minimized,
  isFocused = false,
  onClose,
  onToggleMinimize,
  onFocus,
  panelOffset = 24,
  showBackToActivity,
  onBackToActivity,
}: WorkModalProps) => {
  const [commentDialogOpen, setCommentDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [fullscreen, setFullscreen] = useState(false);

  const {
    data: work,
    isLoading,
    isFetching,
    error,
  } = useGetWorkByIdQuery(workId, {
    skip: !workId,
  });

  const isRefresh = isFetching && !isLoading;

  const workTypeEntry = work
    ? workTypeRegistry[work.Type as WorkTypeEnum]
    : undefined;
  const WorkTypeComponent = workTypeEntry?.component;

  const hiddenEventTypes = workTypeEntry?.hiddenEventTypes;

  const allProcessedEvents = work
    ? parseEventDataArray(work.EventDatas || [])
        .filter((e) => !hiddenEventTypes?.includes(e.Type))
        .sort(
          (a, b) => new Date(b.Time).getTime() - new Date(a.Time).getTime()
        )
    : [];

  const timelineEvents = allProcessedEvents.filter(
    (e) => !FILE_EVENT_TYPES.includes(e.Type)
  );

  return (
    <>
      <Paper
        elevation={isFocused ? 16 : 4}
        onMouseDown={onFocus}
        sx={(theme) => {
          if (fullscreen && !minimized) {
            return {
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              width: "100%",
              height: "100%",
              zIndex: theme.zIndex.modal,
              borderRadius: 0,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              transition: "all 0.3s ease",
              outline: "none",
              opacity: 1,
            };
          }
          return {
            position: "fixed",
            bottom: 0,
            right: panelOffset,
            width: minimized ? MINIMIZED_WIDTH : PANEL_WIDTH,
            height: minimized ? "auto" : "70vh",
            zIndex: isFocused
              ? theme.zIndex.drawer + 1
              : theme.zIndex.drawer - 1,
            borderRadius: "12px 12px 0 0",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            transition: "all 0.3s ease",
            outline: isFocused
              ? `2px solid ${theme.palette.primary.main}`
              : "2px solid transparent",
            opacity: isFocused || minimized ? 1 : 0.85,
            [theme.breakpoints.down("md")]: minimized
              ? {
                  // Minimized on mobile: small bar stacked horizontally
                  width: MOBILE_MINIMIZED_WIDTH,
                  right: panelOffset,
                  borderRadius: "8px 8px 0 0",
                }
              : {
                  // Expanded on mobile: full width, only show focused
                  width: "100%",
                  right: 0,
                  height: "85vh",
                  borderRadius: 0,
                  display: isFocused ? "flex" : "none",
                },
          };
        }}
      >
        <WorkModalHeader
          workId={workId}
          workName={work?.Name}
          customerName={work?.Customer?.Name}
          customerId={work?.Customer?.Id}
          categoryName={work?.Category.Name}
          isRefreshing={isRefresh}
          minimized={minimized}
          fullscreen={fullscreen}
          onToggleMinimize={onToggleMinimize}
          onToggleFullscreen={() => setFullscreen((f) => !f)}
          onClose={onClose}
        />

        <Collapse
          in={!minimized}
          timeout={300}
          sx={{
            flex: !minimized ? 1 : undefined,
            display: !minimized ? "flex" : undefined,
            flexDirection: "column",
            overflow: "hidden",
            "& .MuiCollapse-wrapper, & .MuiCollapse-wrapperInner": {
              display: "flex",
              flexDirection: "column",
              flex: 1,
              overflow: "hidden",
            },
          }}
        >
          {work && <WorkInfoBar work={work} />}

          <Tabs
            value={activeTab}
            onChange={(_, v) => setActiveTab(v)}
            sx={{
              minHeight: 36,
              px: 2,
              borderBottom: "1px solid",
              borderColor: "divider",
              "& .MuiTab-root": {
                minHeight: 36,
                py: 0.5,
                textTransform: "none",
                fontSize: "0.85rem",
              },
            }}
          >
            <Tab label="Översikt" />
            <Tab label="Aktiviteter" />
            {workTypeEntry && <Tab label={workTypeEntry.tabLabel} />}
          </Tabs>

          <Box
            sx={{
              flex: 1,
              overflowY: "auto",
              bgcolor: "grey.50",
              p: 3,
            }}
          >
            {isLoading && (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                minHeight={200}
              >
                <Stack alignItems="center" spacing={2}>
                  <CircularProgress size={48} />
                  <Typography variant="body2" color="text.secondary">
                    Laddar arbetsinformation...
                  </Typography>
                </Stack>
              </Box>
            )}

            {error && (
              <Alert severity="error" sx={{ borderRadius: 2 }}>
                <Typography variant="body1">
                  Kunde inte ladda arbetet. Försök igen senare.
                </Typography>
              </Alert>
            )}

            {work && activeTab === 0 && (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                <NextActivityCard
                  workItems={work.WorkItems}
                  customerNo={work.Customer?.Id}
                  workId={work.Id}
                  workIsOpen={work.State === 1}
                />
                <FilesCard events={allProcessedEvents} workId={work.Id} />
                <CommunicationCard
                  events={timelineEvents}
                  onAddComment={() => setCommentDialogOpen(true)}
                  workId={work.Id}
                />
              </Box>
            )}

            {work && activeTab === 1 && (
              <WorkActivityStream
                workItems={work.WorkItems}
                workId={work.Id}
                customerNo={work.Customer?.Id}
              />
            )}

            {work && activeTab === 2 && WorkTypeComponent && (
              <WorkTypeComponent work={work} />
            )}
          </Box>

          <WorkModalFooter
            work={work}
            showBackToActivity={showBackToActivity}
            onBackToActivity={onBackToActivity}
            onClose={onClose}
          />
        </Collapse>
      </Paper>

      {/* Add Comment Dialog */}
      {work && (
        <AddCommentDialog
          open={commentDialogOpen}
          onClose={() => setCommentDialogOpen(false)}
          workId={work.Id}
          workName={work.Name}
        />
      )}
    </>
  );
};

export default WorkModal;
